import { Story } from '../../types/story';

export const budapestTranslator: Story = {
  id: 'budapest-translator',
  title: 'The Translator',
  excerpt: 'A freelance interpreter assigned to a difficult French diplomat discovers that some things transcend language during a week-long EU summit in Budapest.',
  city: 'Budapest',
  publishDate: '2025-12-03',
  readTime: '28 min read',
  tags: ['♀♂', 'forbidden', 'power play', 'office'],
  categories: ['forbidden', 'intense', 'romantic'],
  intensity: 3,
  content: `The email came on a Tuesday. "High-level assignment. EU summit in Budapest. One week. French delegation. Excellent compensation." I almost deleted it—I'd sworn off diplomatic work after the last disaster—but the number at the bottom made me pause. Fifteen thousand euros for a week's work. That was six months of rent.

I accepted before I could talk myself out of it.

They sent me the briefing documents the next day. The summit was focused on Eastern European energy policy, which meant long days of technical jargon and political posturing. My job was simple: shadow the French delegation's lead negotiator and translate everything—meetings, dinners, private conversations. Be invisible but indispensable.

The lead negotiator was Laurent Beaumont. I googled him immediately. Fifty-two, career diplomat, married to the daughter of a former French president. Educated at Sciences Po and Oxford. Known for being brilliant, ruthless, and impossible to work with. He'd gone through four translators in the past two years.

Perfect.

I flew into Budapest on Sunday night. The summit organizers had booked us at the Four Seasons Gresham Palace, a stunning Art Nouveau building overlooking the Danube. My room was small but elegant, with a view of the Chain Bridge lit up against the night sky.

I met Laurent Beaumont on Monday morning in the hotel lobby. He was exactly what I expected—tall, silver-haired, impeccably dressed in a navy suit that probably cost more than my entire wardrobe. He looked at me with the kind of casual disdain that rich men reserve for service workers.

"You're the translator?" His French was Parisian, clipped and precise.

"Elena Kovács. Yes."

"You're Hungarian?"

"Half. My mother was from Debrecen. My father was Spanish."

"And you studied where?"

"Sorbonne. Then Cambridge."

He raised an eyebrow, surprised. "Your French is excellent. Almost no accent."

"That's why they hired me."

"We'll see." He turned and walked toward the conference room, expecting me to follow. "The first session starts in ten minutes. Try to keep up."

I wanted to throw my briefcase at his head.

The first day was brutal. Eight hours of technical negotiations about natural gas pipelines and renewable energy subsidies. Laurent was exactly as advertised—brilliant, aggressive, and completely uncompromising. He spoke rapidly, switching between French, English, and occasionally German, expecting me to translate seamlessly. I did, but it took every ounce of concentration I had.

By the time we broke for dinner, I was exhausted. Laurent, of course, looked fresh as a daisy.

"You did adequately," he said as we walked back to the hotel. "Better than the last one, at least. She cried on day two."

"How encouraging."

He glanced at me, and I saw the ghost of a smile. "You have a sharp tongue, Mademoiselle Kovács. Be careful. Diplomacy requires discretion."

"I'm a translator, not a diplomat. I just repeat what other people say."

"Is that what you think you do?" He stopped walking and turned to face me. "Translation is interpretation. Every choice you make—which word, which tone—shapes meaning. You're not invisible. You're essential."

I didn't know what to say to that. He seemed to realize he'd revealed something, because he immediately became formal again.

"Dinner is at eight. Informal meeting with the German delegation. Wear something appropriate."

"I know how to dress for dinner, Monsieur Beaumont."

"Do you? Good. I'll meet you in the lobby."

I went back to my room and collapsed on the bed, furious and confused. Who did he think he was? But also—what he'd said about translation, about interpretation—he was right. And he'd seen that in a way most people didn't.

Dinner was at a restaurant in the Castle District, all exposed brick and candlelight. The Germans were friendly, the food was excellent, and Laurent was charming in a way I hadn't seen before. He told stories, asked questions, made everyone feel important. It was a performance, but a masterful one.

I sat slightly behind him, translating when needed, otherwise silent. But I watched him. The way he read the room, adjusted his approach to each person, built consensus without anyone realizing they were being managed. It was fascinating.

After dinner, we walked back to the hotel along the Danube. The city was beautiful at night, all lit bridges and baroque architecture.

"You were good tonight," he said. "You anticipated what I needed before I asked."

"That's my job."

"It's more than that. You understand subtext. Most translators just do words."

"Most translators aren't trained in political science."

"Is that what you studied at Cambridge?"

"International relations. I was going to be a diplomat myself, actually. But I discovered I preferred language to politics."

"Why?"

"Because language is honest. Or it can be, if you use it right. Politics is all performance."

He laughed, a real laugh that transformed his face. "You think I'm performing?"

"I know you are. You were someone completely different with the Germans than you were with me this morning."

"And which one is real?"

"Neither. Both. I don't know yet."

We'd reached the hotel. He stopped at the entrance, studying me with an expression I couldn't read.

"We have an early start tomorrow. Six AM briefing. Don't be late."

"I'm never late."

"Good. Bonne nuit, Elena."

It was the first time he'd used my first name.

The rest of the week followed the same pattern. Long days of negotiations, where Laurent was demanding and brilliant and I worked harder than I'd ever worked to keep up. Evenings where we'd have dinner with various delegations, and I'd watch him perform his diplomatic magic. And late nights where we'd walk back to the hotel, talking about everything except what we were both feeling.

Because something was happening. I felt it every time our hands accidentally brushed passing documents. Every time he looked at me across a conference table and I knew exactly what he was thinking. Every time we argued about translation choices and it felt like foreplay.

On Thursday night, after a particularly tense negotiation with the Polish delegation, we ended up in the hotel bar. It was late, past midnight, and the place was nearly empty.

"You were magnificent today," he said, ordering whisky for both of us. "The way you handled that moment when the Polish minister lost his temper—you softened his words just enough to keep things civil without changing the meaning. That's art."

"I thought you said I should be invisible."

"I was wrong. You're not invisible. You're essential." He took a drink. "I've worked with dozens of translators. You're the first one who's actually made me better at my job."

"Because I keep you from starting international incidents?"

"Because you understand what I'm trying to do. You're not just translating words. You're translating intent."

We sat in silence for a moment. The tension between us was palpable now, impossible to ignore.

"You're married," I said finally.

"Yes."

"Happily?"

"That's a complicated question."

"No, it's not. Either you are or you aren't."

He looked at me, and I saw something raw in his expression. "My wife and I have an understanding. We married for practical reasons—family, politics, social position. We respect each other. We're partners. But we're not in love. We haven't been for years."

"That sounds lonely."

"It is." He finished his drink. "What about you? Is there someone waiting for you in Paris?"

"No. There was, but it ended badly. He wanted someone who'd be content being a diplomat's wife. I wanted to be my own person."

"So we're both alone."

"Yes."

The word hung between us, heavy with implication.

"Elena," he said quietly. "I should tell you that I find you incredibly attractive. And intelligent. And challenging in all the best ways. But I'm also your employer for this week, and I'm married, and this would be complicated in every possible way."

"I know."

"So we should probably say goodnight and go to our separate rooms and pretend this conversation never happened."

"Probably."

Neither of us moved.

"Or," he said, his voice lower now, "we could acknowledge that we're both adults who know exactly what we're doing and what the consequences might be. And we could decide that sometimes, complicated is worth it."

My heart was racing. "Your room or mine?"

"Mine. It's bigger. And more private."

We took separate elevators, met in the hallway outside his suite. He unlocked the door and we went inside, and the moment the door closed behind us, everything changed.

He kissed me hard, pressing me against the wall, his hands in my hair. I kissed him back with all the frustration and desire I'd been holding in all week. We stumbled toward the bedroom, shedding clothes as we went—his jacket, my dress, his shirt, my bra.

"Tell me what you want," he said, his mouth on my neck. "In French. I want to hear you say it."

"Je te veux," I gasped. "I want you. Inside me. Now."

He lifted me onto the desk—the same desk where we'd reviewed briefing documents that morning—and pulled off my underwear. His fingers found me already wet, and he groaned.

"You're so ready for me."

"I've been ready all week."

He undid his belt, pushed down his pants, and I saw he was hard and thick. He pulled a condom from his wallet—prepared, always prepared—and rolled it on. Then he positioned himself at my entrance and pushed in slowly, watching my face.

"Fuck," I moaned. "Yes. Like that."

He started to move, slow at first, then faster. The desk creaked beneath us, papers scattering to the floor. I wrapped my legs around his waist, pulling him deeper, and he responded by fucking me harder, his hands gripping my hips.

"You feel incredible," he groaned. "So tight. So perfect."

"Harder," I demanded. "I'm not fragile. Fuck me like you mean it."

Something shifted in his expression—surprise, then hunger. He pulled out, flipped me over so I was bent over the desk, and entered me from behind. This angle was deeper, more intense, and I cried out.

"Like this?" he asked, his voice rough. "Is this what you want?"

"Yes. God, yes. Don't stop."

He didn't. He fucked me hard and fast, one hand tangled in my hair, the other reaching around to play with my clit. The combination was overwhelming, and I felt my orgasm building.

"I'm going to come," I gasped.

"Do it. Come for me. Let me feel it."

I did, crying out in a mix of French and Hungarian, my whole body shaking. He followed moments later, groaning my name, his body shuddering against mine.

We stayed like that for a moment, both breathing hard. Then he pulled out gently and helped me stand. My legs were shaky.

"That was..." he started.

"Unprofessional?"

"I was going to say incredible."

We moved to the bed, and he pulled me against him. For a while, we just lay there, catching our breath.

"I should probably feel guilty," I said.

"But you don't?"

"No. Should I?"

"I don't know. I don't feel guilty either. I feel... alive. I haven't felt like this in years."

We made love again, slower this time, exploring each other properly. He was attentive and skilled, paying attention to what made me gasp, what made me arch into his touch. When he went down on me, using his tongue with the same precision he used in negotiations, I came so hard I saw stars.

Afterward, we lay tangled together, talking in the dark. About our lives, our regrets, our dreams. He told me about his marriage, about the loneliness of living with someone you respected but didn't love. I told him about my ex, about choosing my career over a relationship that would have diminished me.

"What happens after tomorrow?" I asked. The summit ended on Friday. We'd both be flying home.

"I don't know. This was supposed to be just one night."

"Was it?"

"I don't think so. But I also don't know what else it can be. I'm married. You live in Paris. My life is complicated."

"I know."

"But I don't want this to end."

"Neither do I."

We fell asleep eventually, wrapped around each other. When I woke up at dawn, he was already awake, watching me.

"Good morning," he said softly.

"Good morning."

"I have a proposal. And I want you to think about it seriously before you answer."

"Okay."

"I need a permanent translator. Someone who understands not just language but politics, strategy, subtext. Someone I can trust completely. The position would be based in Paris, but there would be travel. A lot of it. And we'd be working together constantly."

"Laurent—"

"Let me finish. I know this is complicated. I know we just crossed a line that makes everything messy. But I also know that you're the best translator I've ever worked with, and I don't want to lose that. So here's what I'm proposing: we work together professionally. We maintain boundaries. And we see what happens."

"You want me to be your translator and your... what? Occasional lover?"

"I want you in my life. However that works. I'm not asking you to be my mistress or to wait around for stolen moments. I'm asking you to be my partner—professionally, and maybe, when circumstances allow, personally too."

"That's insane."

"Probably. But is it more insane than never seeing each other again?"

I thought about it. About going back to Paris and freelancing, taking whatever jobs came my way. About never seeing him again, never feeling the way I'd felt last night. About playing it safe versus taking a risk.

"I'll need my own apartment," I said finally. "And full benefits. And I'm not going to be some secret. If we're doing this, we're honest about it—at least with each other."

He smiled. "Deal. When can you start?"

"I need two weeks to wrap up my current commitments."

"Perfect. I'll have my office send over the contract."

We had one more day together in Budapest. One more day of negotiations and dinners and stolen glances across conference tables. And one more night in his bed, making love like we had all the time in the world instead of just hours.

I started working for Laurent three weeks later. It's been six months now, and it's exactly as complicated as we both knew it would be. We work together brilliantly—he's still demanding and impossible, and I'm still the only one who can keep up with him. And sometimes, when circumstances allow, we're more than colleagues.

His wife knows, I think. Or suspects. She's never said anything, but there's a knowing look in her eyes when we're all at the same event. Maybe she doesn't care. Maybe she has her own arrangements. I don't ask.

Is it perfect? No. Is it conventional? Definitely not. But it's real, and it's ours, and it's better than the alternative—which is a life without him in it.

Sometimes the most honest thing you can do is admit that you want something complicated. That you're willing to live in the grey areas, to make unconventional choices, to build something that doesn't fit any traditional mold.

And sometimes, the best translation isn't the most literal one. Sometimes it's the one that captures the spirit, the intent, the truth beneath the words.

Laurent taught me that. And I'm still learning.`
};
